/**
 * FISHING GEAR + STATS — the tackle the player equips, and the five stats it resolves into.
 *
 * The rig is four slots (ROD / REEL / HANDLE / HOOK) plus one attachment (LURE). The rig carries TACKLE
 * POWER, which is a hard gate: a fish with `needsPower` cannot be hooked until the rig's total reaches it,
 * so upgrading the rig is what opens bigger fish rather than merely making them easier. The lure is a
 * different kind of gate — each carries a FAMILY id, and a fish with `needsLure` bites only when that family
 * is equipped. Two gates of different shapes, so gear progress is never one number going up.
 *
 * THE FIVE STATS are numeric POINTS, and every stat resolves the same way:
 *
 *     value = base + (fishing level − 1) × perLevel + points from equipped gear
 *
 * Levelling raises every stat a little; gear specialises. That split is deliberate and load-bearing — levels
 * should always be worth something, and gear should always be the bigger lever end-game, without either
 * making the other irrelevant. The three of them (base, perLevel, roll ranges in gearUpgrade.ts) are tuned
 * against each other and against `statEffect`'s curve; moving one alone breaks the relationship.
 *
 * WHAT EACH STAT DOES, and where it lands:
 *  • biteRate     — a shorter wait before the bite. Divides the bite delay at the cast site. It does NOT
 *                   change WHETHER a cast bites: `pickFishByChance` rolls that from the fish chances alone,
 *                   so no screen may describe this stat as a bite chance (the tackle readout once did).
 *  • lineStrength — raises the snap point and softens escape-gauge fill (`applyLoadoutToFight`).
 *  • power        — drains a hooked fish's stamina faster (`applyLoadoutToFight`). Not tackle power.
 *  • luck         — tilts the which-fish roll toward rarer fish, and lends ~35% of its magnitude to biteRate.
 *  • intuition    — how often fish-shadows appear and how much their hover preview reveals.
 *
 * FOUR OF THE FIVE ARE MULTIPLIERS resolved by `statEffect`: a saturating curve, so early points are worth
 * much more than late ones and no stat can run away. INTUITION IS NOT — it resolves through
 * `intuitionFraction` to a 0..1 progress fraction, because the shadow sim wants "how far along is this
 * player", not "× what". Reading it as a multiplier is the one mistake this file invites; see that function.
 *
 * The catalogue below is real and complete: 8 rig tiers × 4 slots + 8 lures, unlocking across levels 1–50.
 * Its unlock levels are tuned against the XP curve rather than spaced evenly (see RIG_TIERS), and lure prices
 * against how many fish each family gates (see LURE_ITEMS). A piece's authored `stats` are only a FALLBACK —
 * once the player opens a piece in the Gear Upgrade modal, its rolled lines replace them (see gearUpgrade.ts).
 *
 * THE RIG IS AN UPGRADE TRACK, NOT A SHOP. Each of the four rig slots holds ONE piece at a tier of 1..8 and
 * gold buys the NEXT tier in place; the eight names per slot are rungs, not eight products to collect. The
 * LURE is the exception and stays bought-and-swapped, because it gates by family rather than by power. See
 * the UPGRADE TRACK section (`RigTiers`, `nextUpgrade`, `canUpgrade`) — it is where the rig's progress lives.
 */

import type { GearRoll } from "./gearUpgrade";

// ---- SLOTS -----------------------------------------------------------------

export type GearSlot = "rod" | "reel" | "handle" | "hook" | "lure";

/** The four RIG slots — the ones that carry tackle power and climb the upgrade track. The LURE is deliberately
 *  not one of them: it is a family gate, not a ladder (see `RIG_SLOT_IDS` and the upgrade-track section). */
export type RigSlot = "rod" | "reel" | "handle" | "hook";

export interface GearSlotDef {
  id: GearSlot;
  name: string;
  /** emoji shown in the empty slot + as the item fallback. */
  icon: string;
  /** one-line description of what this slot governs. */
  blurb: string;
  /** LURE is an equippable attachment (a fish-attractant, NOT a consumable); rod/reel/line/hook are the rig.
   *  Purely for grouping in the UI. */
  group: "rig" | "attachment";
}

/** The equip slots, in display order. Rod → reel → handle → hook form the RIG; the LURE attaches to it.
 *  (There is no consumable bait — lures are equippable gear.) */
export const GEAR_SLOTS: GearSlotDef[] = [
  { id: "rod",  name: "Rod",  icon: "🎣", group: "rig",        blurb: "Sets your tackle power and how much line tension you can take." },
  { id: "reel", name: "Reel", icon: "⚙️", group: "rig",        blurb: "Sets how fast you wear a fish down in the reel-in fight." },
  { id: "handle", name: "Handle", icon: "🖐️", group: "rig",    blurb: "Raises your Intuition, so you spot more fish shadows and learn more from them." },
  { id: "hook", name: "Hook", icon: "🪝", group: "rig",        blurb: "Shortens the wait for a bite and adds to your tackle power." },
  { id: "lure", name: "Lure", icon: "🎏", group: "attachment", blurb: "A charm or spinner that nudges your luck toward rarer fish. Some fish bite only a specific lure." },
];

export function gearSlot(id: GearSlot): GearSlotDef {
  return GEAR_SLOTS.find((s) => s.id === id)!;
}

// ---- STATS -----------------------------------------------------------------
//
// Points are unbounded in principle; in practice a bare Lv 1 player sits at 9–15 per stat and a fully-kitted
// Lv 50 one reaches 270–300. `statEffect`'s curve is scaled to that real span — see the header.

// The five fishing stats:
//  • biteRate     — a shorter wait before the bite (speed only — never whether a cast bites).
//  • lineStrength — line tension tolerance (pull farther before snapping) + slight escape-fill reduction.
//  • power        — how fast the fish's stamina drains, especially under tension.
//  • luck         — skews catch rates toward rarer fish.
//  • intuition    — how often shadow spots appear + how much the hover-over-shadow modal reveals.
export type FishingStatId = "biteRate" | "lineStrength" | "power" | "luck" | "intuition";

export interface FishingStatDef {
  id: FishingStatId;
  name: string;
  icon: string;
  /** what it does, in the player's terms. */
  blurb: string;
  /** starting points at Fishing Lv 1 with no gear. */
  base: number;
  /** points gained per Fishing level (levelling nudges every stat up). */
  perLevel: number;
}

// PER-LEVEL growth was 0.5–0.7, which over the 49 levels to the cap totalled 25–34 points. Against a stat
// curve that gear pushed to ~765 points that was indistinguishable from zero — levels stopped mattering. It
// is raised to 1.3–1.7 (≈64–83 points across the climb) so a level is a visible share of the total at every
// stage: roughly 79% of a stat at Lv 10, about half at Lv 25, and a quarter of a fully-kitted stat at Lv 50.
// Gear still leads end-game, which is correct — it just no longer drowns levelling out entirely.
export const FISHING_STATS: FishingStatDef[] = [
  { id: "biteRate",     name: "Bite Rate",     icon: "⏱️", base: 10, perLevel: 1.5, blurb: "A shorter wait before a bite, so more casts in the same time." },
  { id: "lineStrength", name: "Line Strength", icon: "🧵", base: 10, perLevel: 1.7, blurb: "Tension your line takes before snapping, and less escape-gauge fill when on the right side." },
  { id: "power",        name: "Power",         icon: "💪", base: 10, perLevel: 1.7, blurb: "How fast the fish's stamina drains, especially under tension." },
  { id: "luck",         name: "Luck",          icon: "🍀", base: 5,  perLevel: 1.3, blurb: "Skews catch rates toward rarer, more valuable fish." },
  { id: "intuition",    name: "Intuition",     icon: "🔮", base: 5,  perLevel: 1.3, blurb: "How often fish-shadows appear, and how much the shadow preview reveals." },
];

export function fishingStat(id: FishingStatId): FishingStatDef {
  return FISHING_STATS.find((s) => s.id === id)!;
}

/** A resolved fishing-stat readout — the integer POINTS value + its split by source (for the tooltip/UI). */
export interface FishingStatValue {
  id: FishingStatId;
  /** total resolved points (base + level + gear). */
  value: number;
  /** the breakdown, so the UI can show "10 base + 8 level + 15 gear". */
  base: number;
  fromLevel: number;
  fromGear: number;
}

/**
 * Map a stat's POINTS to the multiplier the game knob uses. Points 0 ⇒ ~baseline (mult ≈ 1); higher points ⇒
 * a stronger effect with gentle diminishing returns. Each knob has its own ceiling so no stat runs away.
 *  • biteRate/lineStrength/power → a >1 multiplier (faster bites / higher snap point / faster fight).
 *  • luck → returned as its multiplier too; the caller splits it into a bite-speed nudge + a rarity tilt.
 *
 * INTUITION is deliberately absent from that list even though it has an entry below: nothing reads its
 * multiplier. It resolves through `intuitionFraction` instead — the entry here exists only so the CURVE
 * table stays total over FishingStatId.
 */
export function statEffect(id: FishingStatId, points: number): number {
  const p = Math.max(0, points);
  // saturating curve: mult = 1 + gain·(1 − e^(−p/scale)); gain = the max bonus, scale = how fast it climbs.
  //
  // SCALE is the number to be careful with. It is the points at which ~63% of `gain` is spent, so the curve is
  // effectively finished by about 3× it. The old scales of 55–65 meant a stat was done by ~200 points — but a
  // fully-kitted L50 player carries ~765 points per stat (25 gear lines, see gearUpgrade's `rollRange`), so
  // every stat sat pinned at 100.0% of its curve and a fishing level was worth a measured 0.000%. Levelling
  // had become a pure unlock gate with no mechanical weight, which is the opposite of the model above.
  //
  // Stretched to 150–175 so the curve's useful region reaches roughly where gear actually lands. Paired with
  // the reduced roll ranges this puts a kitted L50 angler near 80% of each curve — clearly stronger than a
  // bare one, none of it pinned, and a level still moving a visible number at every stage. These scales and
  // `rollRange`'s coefficients are tuned against each other; changing one alone re-breaks the relationship.
  const CURVE: Record<FishingStatId, { gain: number; scale: number }> = {
    biteRate:     { gain: 0.9,  scale: 150 },   // up to ~+90% faster bites (the wait is divided by this)
    lineStrength: { gain: 1.1,  scale: 165 },   // up to ~+110% snap tolerance
    power:        { gain: 1.0,  scale: 165 },   // up to ~+100% stamina-drain speed
    luck:         { gain: 1.2,  scale: 175 },   // luck's raw magnitude (split by the caller)
    intuition:    { gain: 1.0,  scale: 165 },   // shadow spawn rate + reveal depth
  };
  const { gain, scale } = CURVE[id];
  return 1 + gain * (1 - Math.exp(-p / scale));
}

/**
 * INTUITION → a 0..1 FRACTION, the one stat that does not read as a multiplier. It drives the shadow sim's
 * spawn cadence, active cap and reveal depth (fishShadows.ts, FishShadowPreview.tsx), all of which want "how
 * far along is this player" rather than "× what".
 *
 * It gets its OWN curve rather than reusing `statEffect`, because the two answer different questions. Running
 * intuition through `statEffect` and then re-normalising by `(mult - 1) / gain` inherited that curve's
 * asymptote: the result approached 1.0 without reaching it, so a fully-kitted L50 angler topped out around
 * 0.81 and the authored end of the shadow range — the 6-second gap, the cap of 6 — was decorative. It also
 * started at essentially 0, so a new player saw a shadow about once every two and a half minutes and could
 * reasonably conclude the feature did not exist.
 *
 * SCALE 90 is the number to be careful with. It places the stages roughly at L1 0.06 / L10 0.24 / L25 0.57 /
 * L50 0.95 — a climb across the whole game rather than a switch, crossing the 0.5 reveal threshold in the
 * mid-game where the preview starting to read unknown odds is a real step up. Lower it and the top of the
 * range arrives early and flattens; raise it and the last levels stop paying out.
 */
const INTUITION_SCALE = 90;
export function intuitionFraction(points: number): number {
  return 1 - Math.exp(-Math.max(0, points) / INTUITION_SCALE);
}

// ---- GEAR ITEMS (catalogue comes later; the shape is defined now) ----------

/** A single equippable piece of tackle. Each modifies one or more fishing stats. The catalogue (the actual
 *  rods/reels/lines/baits) is authored later — this type + the derivation below are the contract they fill. */
export interface GearItem {
  id: string;
  slot: GearSlot;
  name: string;
  icon: string;
  /** flavour / what it does. */
  blurb: string;
  /** the FISHING LEVEL at which this piece unlocks (becomes buyable). 0/absent = available from the start. */
  unlockLevel?: number;
  /** coin cost to BUY this piece once it's unlocked. */
  price?: number;
  /** TACKLE POWER this piece contributes. The player's total power = sum over equipped rig pieces (rod/reel/
   *  line/hook). A fish with `needsPower` can't be hooked until total power ≥ it. Lures add 0 power. */
  power?: number;
  /** per-stat POINT contributions (additive, e.g. { power: 12 }). Absent = no effect. A piece's ROLLED lines
   *  replace these once it is opened in the Gear Upgrade modal — see `gearStatPoints`. */
  stats?: Partial<Record<FishingStatId, number>>;
  /** LURE only: a stable LURE FAMILY id (e.g. "spinner", "fly", "jig"). A fish with `needsLure` bites only
   *  when a lure of that family is equipped. Also drives the attraction note. */
  lureId?: string;
  /** for the LURE: a short label describing what it attracts (fills the "Attraction" note). */
  attracts?: string;
}

/**
 * THE CATALOGUE — a tiered ladder across the 4 RIG slots (rod/reel/line/hook) + a line of LURES, unlocking
 * roughly ONE piece per fishing level (cap 50). Each rig piece carries TACKLE POWER (the fish-gating stat) and
 * stat multipliers themed to its slot; each lure carries a `lureId` family + a luck bonus and gates the fish
 * that bite only that lure. Prices rise with tier. Everything here is data — edit freely.
 *
 * Progression shape: 8 rig tiers × 4 slots = 32 rig pieces, interleaved so a new piece opens every ~1–2
 * levels; plus 8 lures sprinkled in. Total ≈ 40 unlocks across levels 1–50 (the tail levels are for the top
 * lures + a little breathing room). Total rig power climbs from 4 (the free starter set) to 48 fully kitted,
 * which is exactly what it takes to hook the hardest fish in the roster — see RIG_TIERS for why 48.
 */

// RIG TIERS — [rod, reel, handle, hook] flavour names per tier, with the tier's unlock level + per-piece
// power. Each slot's themed stat lives in RIG_STATS (rod→line strength, reel→power, handle→intuition,
// hook→bite rate); that theming is the same order the names are listed in here.
interface RigTier { tier: number; unlock: number; power: number; price: number; names: [string, string, string, string]; }
// PRICES are PER-PIECE (a full rig = 4 slots ≈ 4× this) and read as the cost to UPGRADE INTO a tier, so a
// player pays every rung on the way up rather than skipping to the one they saved for. The target is the same
// one this column has always had, measured against the fish-progression sim at the parity-balanced fishing
// income: bringing the current-best full set up a tier costs ~15 min of fishing at the early tiers, rising to
// ~2 h for the top prestige tier — a steady "fish for your next upgrade" loop, neither trivial nor a wall.
//
// These came DOWN from 700/1500/2400/3800/6500/11000/19000 when the shop became the upgrade track. Those were
// tuned when a price meant buying one piece OUTRIGHT, so under the track they were being charged per rung:
// the cumulative cost of one slot to tier 8 was 44,900 against the 19,000 that column was set for. Re-running
// `simulateProgression`, though, the binding constraint was not the top — a tier-8 set at the old price was
// 176 min of fishing but the player reaches L48 with ~770k banked, 10× over. It was the BOTTOM: tiers 3, 4
// and 5 each cost more than a player has earned by their unlock level, so gold rather than level set the pace
// there (tier 3 arrived 5 levels late, tier 4 four late). The curve below re-lands each tier on the minute
// target above, which cuts the worst lag to 3 levels and puts tiers 5-8 back on their unlock level exactly.
// The ~3-level lag left at tier 2 is the EARLY INCOME curve, not the price — levels 1-5 earn ~1.5k total, so
// erasing it would mean making the first upgrade nearly free. Leave it.
// POWER per piece is the load-bearing column here, and it is tuned against the fish roster, NOT chosen for
// its shape. A full rig is 4× it (every slot in a tier carries the same power), and the highest `needsPower`
// in DEFAULT_FISH_CONDS is the Aetherling at 46 — so tier 8's 12/piece = 48 is the FIRST rig that clears the
// top gate, and tier 7's 40 deliberately falls short of it. The 2 points of slack at the top are structural:
// 46 is not divisible by 4, so 48 is the smallest equal-piece rig that can reach it.
//
// The old ladder ran 1,2,3,5,7,9,12,16 → a full rig of 64 against a roster topping out at 46. Tier 7 alone
// hit 48 and already cleared every fish in the game, which made tier 8 a 19,000-coin purchase that unlocked
// literally nothing — pure number with nothing behind it. Every tier below it opens at least one fish that
// the tier beneath cannot hook; if you re-tune these, check that property still holds.
//
// UNLOCK LEVELS are tuned against the XP CURVE, not spaced evenly in levels. The fishing curve (level.ts
// FISH_CURVE) makes a late level cost far more than an early one — on curve 2 the climb reaches L22 (tier 5) at
// ≈8.5 h of fishing, L30 (tier 6) at ≈23 h, L40 (tier 7) at ≈63 h and L48 (tier 8) at ≈127 h, of ≈150 h to L50.
// The tail used to place the last rig at L46 and leave L46→50 buying a single lure; tier 7 moved 38→40 and tier 8
// 46→48 so the hardest stretch of the curve is also where the last gear lands. Moving these earlier re-opens
// that dead tail; check `simulateProgression` in fishProgressionSim.ts after any change.
//
// PRICES vs TIME. The prices were sized as ~15 min of fishing income at the early tiers rising to ~2 h at the
// top, when the levels between tiers took about that long. On curve 2 the levels take far longer than the coins,
// so a tier is now gated by its LEVEL, not its price — a deliberate consequence of the re-curve, not a drift.
const RIG_TIERS: RigTier[] = [
  { tier: 1, unlock: 1,  power: 1,  price: 0,      names: ["Willow-Branch Rod", "Handline Spool", "Bare Cork Grip", "Bent-Pin Hook"] },
  { tier: 2, unlock: 5,  power: 2,  price: 650,    names: ["Cane Pole", "Click Reel", "Wrapped Cork Handle", "Bronze Hook"] },
  { tier: 3, unlock: 10, power: 3,  price: 1000,   names: ["Bamboo Rod", "Spincast Reel", "Cork-&-Foam Grip", "Barbed Hook"] },
  { tier: 4, unlock: 16, power: 4,  price: 1750,   names: ["Fiberglass Rod", "Baitcaster", "EVA-Foam Handle", "Circle Hook"] },
  { tier: 5, unlock: 22, power: 6,  price: 2850,   names: ["Graphite Rod", "Sealed Spinning Reel", "Contoured Split-Grip", "Tempered Hook"] },
  { tier: 6, unlock: 30, power: 8,  price: 4700,   names: ["Carbon Surf Rod", "Twin-Drag Reel", "Fighting-Butt Grip", "Forged Treble"] },
  { tier: 7, unlock: 40, power: 10, price: 7800,   names: ["Nano-Carbon Rod", "Lever-Drag Reel", "Gimbal Fighting Butt", "Razor Hook"] },
  { tier: 8, unlock: 48, power: 12, price: 13000,  names: ["Gilded Rod", "Clockwork Reel", "Carved Onyx Grip", "Diamond-Tipped Hook"] },
];
const RIG_ICON: Record<GearSlot, string> = { rod: "🎣", reel: "⚙️", handle: "🖐️", hook: "🪝", lure: "🎏" };
const RIG_BLURB: Record<RigSlot, string> = {
  rod:  "More power to hook bigger fish, and a stronger line.",
  reel: "Wears a fish down faster in the fight.",
  handle: "A better grip that raises your Intuition.",
  hook: "A sharper hook that shortens the wait for a bite.",
};
// Per-piece stat POINTS (additive) — the FALLBACK stats for an un-rolled piece (the Gear Upgrade rolls replace
// these once a piece is opened). Each slot has a PRIMARY themed stat (rod→line strength, reel→power,
// handle→intuition, hook→bite rate) plus a splash of LUCK (the wildcard). Scales with tier so late gear stacks
// high; early tiers are small.
const RIG_STATS: Record<RigSlot, (t: number) => Partial<Record<FishingStatId, number>>> = {
  rod:    (t) => ({ lineStrength: t * 5, luck: t }),   // primarily LINE STRENGTH
  reel:   (t) => ({ power: t * 5, luck: t }),          // primarily POWER
  handle: (t) => ({ intuition: t * 5, luck: t }),      // primarily INTUITION
  hook:   (t) => ({ biteRate: t * 5, luck: t }),       // primarily BITE RATE
};

// A distinct ICON per catalogue piece (per slot × tier), so every attachment reads uniquely in the slot/swap/
// shop — climbing from humble to ornate. (The garden-scene rod draws each part procedurally by tier; this is
// the pip shown in menus.)
const RIG_TIER_ICON: Record<RigSlot, string[]> = {
  rod:    ["🌿", "🎋", "🎍", "🎣", "📏", "🏹", "🛰️", "✨"],   // willow → cane → bamboo → fibreglass → graphite → carbon → nano → gilded
  reel:   ["🧵", "🎡", "⚙️", "🔩", "🌀", "🛞", "🕹️", "🕰️"],   // handline → click → spincast → baitcaster → spinning → twin-drag → lever → clockwork
  handle: ["🪵", "🧶", "🧽", "🖐️", "✊", "🥊", "🔧", "💠"],   // cork → wrapped → foam → EVA → split → fighting → gimbal → onyx
  hook:   ["📌", "🪝", "🥢", "⭕", "🗡️", "🔱", "🪒", "💎"],   // bent-pin → bronze → barbed → circle → tempered → treble → razor → diamond
};

const RIG_ITEMS: GearItem[] = RIG_TIERS.flatMap((r) => {
  const slots: RigSlot[] = ["rod", "reel", "handle", "hook"];
  return slots.map((slot, i): GearItem => ({
    id: `${slot}${r.tier}`, slot, name: r.names[i]!, icon: RIG_TIER_ICON[slot][r.tier - 1] ?? RIG_ICON[slot], blurb: RIG_BLURB[slot],
    unlockLevel: r.unlock, price: r.price, power: r.power, stats: RIG_STATS[slot](r.tier),
  }));
});

// LURES — equippable attachments (NO consumable bait). Each has a `lureId` FAMILY that some fish require, plus
// a luck bonus toward rarer fish. Unlock across the levels between the rig tiers.
//
// The last two unlock at 46 and 50 so they interleave with the rig tiers now ending at 48. See RIG_TIERS for
// why the late unlocks were pushed back: the fishing XP curve puts most of its mass in the last few levels,
// so unlocks spaced evenly in LEVELS are wildly uneven in EFFORT.
//
// PRICE TRACKS WHAT THE LURE OPENS — how many fish need its family, and how good they are. That relationship
// used to be INVERTED at the top: price correlated −0.19 with fish count, because it was set by tier position
// instead. The Void Siren cost 32,000 at L50 to gate 2 fish (16,000 per fish, bought with 0 hours of game
// left to use it) while the Silver Spoon gated 5 for 6,500; the Popper cost 10,000 for a SINGLE fish.
//
// Fixed from both ends: the void and popper families gained fish (see fishConds.ts — the Lanternjaw and
// Inkfin now answer the void, the Longnose Gar the popper), and the late prices came down to match. The Void
// Siren also moved L50 → 46, so there are ~13 hours of play left to actually use it; the Prism takes the L50
// slot so the climb still ends on an unlock rather than a dead tail. Price now correlates +0.79 with the coin
// value a lure's fish are worth. Re-check that number if you re-price or re-gate anything here.
const LURE_ITEMS: GearItem[] = [
  { id: "lureSpinner", slot: "lure", name: "Brass Spinner", icon: "🎏", unlockLevel: 3,  price: 1000,  lureId: "spinner", attracts: "bream and largemouth bass", stats: { luck: 6 },  blurb: "A spinning blade that flashes and draws curious panfish. Some fish bite only a spinner." },
  { id: "lureFly",     slot: "lure", name: "Feather Fly",   icon: "🪶", unlockLevel: 8,  price: 1900,  lureId: "fly",     attracts: "trout and surface feeders", stats: { luck: 9 },  blurb: "A hand-tied fly that rides the surface, a favorite of trout. Some fish bite only a fly." },
  { id: "lureJig",     slot: "lure", name: "Lead Jig",      icon: "🎣", unlockLevel: 14, price: 4000,  lureId: "jig",     attracts: "bottom-dwellers and catfish", stats: { luck: 12 }, blurb: "A weighted jig that works the bottom for whiskered fish. Some fish bite only a jig." },
  { id: "lureSpoon",   slot: "lure", name: "Silver Spoon",  icon: "🥄", unlockLevel: 20, price: 6500,  lureId: "spoon",   attracts: "pike and big predators", stats: { luck: 15 }, blurb: "A wobbling spoon that looks like a fleeing baitfish, which predators can't resist. Some fish bite only a spoon." },
  { id: "lurePopper",  slot: "lure", name: "Popper Plug",   icon: "🫧", unlockLevel: 28, price: 7000,  lureId: "popper",  attracts: "aggressive surface strikers", stats: { luck: 19 }, blurb: "A noisy surface popper that calls up aggressive strikes. Some fish bite only a popper." },
  { id: "lureGlow",    slot: "lure", name: "Glowbug Charm",  icon: "✨", unlockLevel: 36, price: 9000,  lureId: "glow",    attracts: "night and fantasy fish", stats: { luck: 24 }, blurb: "A softly glowing charm for the dark and the strange. Some fish bite only a glowbug." },
  { id: "lureVoid",    slot: "lure", name: "Void Siren",    icon: "🕳️", unlockLevel: 46, price: 20000, lureId: "void",    attracts: "the abyssal legends", stats: { luck: 38 }, blurb: "A lure that hums to things from the deep dark. The abyssal legends answer only this." },
  { id: "lurePrism",   slot: "lure", name: "Prism Lure",    icon: "🌈", unlockLevel: 50, price: 15000, lureId: "prism",   attracts: "shimmering rarities", stats: { luck: 30 }, blurb: "Splits the light into a shimmer only the rarest fish chase. Some fish bite only a prism lure." },
];

/** The catalogue — the tiered rig ladder + the lure line. */
export const GEAR_ITEMS: GearItem[] = [...RIG_ITEMS, ...LURE_ITEMS];
const GEAR_BY_ID = new Map(GEAR_ITEMS.map((g) => [g.id, g]));
export function gearItem(id: string): GearItem | undefined { return GEAR_BY_ID.get(id); }

/** Everything that becomes BUYABLE at exactly this fishing level: the lure whose `unlockLevel` is this
 *  level, plus the four rig pieces of any tier gated to it. Tier 1 is excluded — it is the free starter kit
 *  every player already holds at level 1, so announcing it would be announcing nothing.
 *
 *  Reads GEAR_ITEMS itself rather than re-listing the levels, so the level-up modal and the shop's
 *  `unlockLevel` gate can never drift apart. Rig pieces come back as a whole TIER (one entry for the four
 *  slots) because that is how the shop sells them and how the player thinks about them. */
export function gearUnlockedAt(level: number): { lures: GearItem[]; rigTier: number | null; rigNames: string[]; rigPrice: number; rigPower: number } {
  const lures = GEAR_ITEMS.filter((g) => g.slot === "lure" && g.unlockLevel === level);
  const tier = RIG_TIERS.find((r) => r.unlock === level && r.tier > 1) ?? null;
  return {
    lures,
    rigTier: tier?.tier ?? null,
    rigNames: tier?.names ?? [],
    rigPrice: tier?.price ?? 0,
    rigPower: tier?.power ?? 0,
  };
}

/** Items equippable in a given slot (respecting an optional level gate). */
export function itemsForSlot(slot: GearSlot, level: number): GearItem[] {
  return GEAR_ITEMS.filter((g) => g.slot === slot && (g.unlockLevel ?? 0) <= level);
}

/** The free STARTER piece for a rig slot (tier-1, price 0) — every player has this by default so tackle power
 *  is never 0 (see effectiveEquipped). Lures have no starter (you fish lure-less until you buy one). */
export function starterItem(slot: GearSlot): GearItem | undefined {
  return GEAR_ITEMS.find((g) => g.slot === slot && (g.unlockLevel ?? 0) <= 1 && (g.price ?? 0) === 0);
}

/** The player's effective equipped set: their choices, with any EMPTY RIG slot filled by the free starter
 *  piece so a bare player still holds a basic rod/reel/line/hook (power > 0). Lures stay as chosen (may be none). */
export function effectiveEquipped(equipped: EquippedGear): EquippedGear {
  const out: EquippedGear = { ...equipped };
  for (const slot of ["rod", "reel", "handle", "hook"] as GearSlot[]) { if (!out[slot]) { const s = starterItem(slot); if (s) out[slot] = s.id; } }
  return out;
}

/** The player's equipped set: slot → item id. Absent slot = empty. */
export type EquippedGear = Partial<Record<GearSlot, string>>;

/** Sum the POINTS an equipped set contributes to each stat (additive — every item's stat points add up).
 *  A piece's ROLLED stats (from the Gear Upgrade system, keyed by item id in `rolls`) REPLACE its old static
 *  `stats`; a piece with no roll (e.g. a lure, or one never opened in the upgrade modal) falls back to its
 *  authored `stats`. */
function gearStatPoints(equipped: EquippedGear, rolls?: Record<string, GearRoll>): Record<FishingStatId, number> {
  const pts: Record<FishingStatId, number> = { biteRate: 0, lineStrength: 0, power: 0, luck: 0, intuition: 0 };
  for (const slot of GEAR_SLOTS) {
    const id = equipped[slot.id]; if (!id) continue;
    const roll = rolls?.[id];
    if (roll) { for (const s of roll.stats) pts[s.id] += s.value; continue; }
    const item = gearItem(id); if (!item?.stats) continue;
    for (const [k, v] of Object.entries(item.stats)) pts[k as FishingStatId] += v;
  }
  return pts;
}

/** The equipped ROD's TIER (1..8), used to STYLE the rod drawn in the fishing minigame so upgrades show.
 *  Falls back to the free starter (tier 1) when no rod is equipped. Rod ids are `rod1`..`rod8`. */
export function equippedRodTier(equipped: EquippedGear): number {
  const id = effectiveEquipped(equipped).rod;
  const m = id ? /rod(\d+)/.exec(id) : null;
  const t = m ? parseInt(m[1]!, 10) : 1;
  return Math.max(1, Math.min(8, t));
}

/** The tier (1..8) of a rig slot's equipped item (rod/reel/handle/hook), from ids like `reel3`. Starter = 1.
 *  This is the READ side of the upgrade track: a saved `EquippedGear` is decoded back to four tier numbers by
 *  `rigTiers`, which calls this per slot. Anything unparseable clamps to tier 1 rather than throwing. */
export function rigTierOf(equipped: EquippedGear, slot: RigSlot): number {
  const id = effectiveEquipped(equipped)[slot];
  const m = id ? new RegExp(`${slot}(\\d+)`).exec(id) : null;
  return clampTier(m ? parseInt(m[1]!, 10) : 1);
}

/** Modular appearance of the assembled rod — the per-PART tiers + the lure family — so the minigame can draw
 *  each equipped piece (shaft, reel, guides, terminal lure) differently as the player upgrades. */
export interface RodAppearance {
  rodTier: number;    // 1..8 — blank material + telescopic section count/length
  reelTier: number;   // 1..8 — the reel body/spool
  handleTier: number; // 1..8 — the grip/butt section (cork → foam → fighting-butt)
  hookTier: number;   // 1..8 — the terminal hook look
  lure: string | null; // lureId family ("spinner"/"fly"/… ) or null if no lure equipped
}
export function rodAppearance(equipped: EquippedGear): RodAppearance {
  const eff = effectiveEquipped(equipped);
  const lure = eff.lure ? (gearItem(eff.lure)?.lureId ?? null) : null;
  return {
    rodTier: rigTierOf(equipped, "rod"),
    reelTier: rigTierOf(equipped, "reel"),
    handleTier: rigTierOf(equipped, "handle"),
    hookTier: rigTierOf(equipped, "hook"),
    lure,
  };
}

/** The short attraction note (what the equipped lure draws in). */
export function attractionNote(equipped: EquippedGear): string {
  const it = equipped.lure ? gearItem(equipped.lure) : undefined;
  return it?.attracts ? `Draws ${it.attracts}` : "Any fish · no lure equipped";
}

/**
 * Resolve the player's fishing stats as numeric POINTS: value = the stat's BASE + FISHING LEVEL × its per-level
 * growth + points from equipped GEAR. Returns the total plus its by-source breakdown (for the UI/tooltip).
 */
export function deriveFishingStats(equipped: EquippedGear, fishingLevel: number, rolls?: Record<string, GearRoll>): FishingStatValue[] {
  const gear = gearStatPoints(equipped, rolls);
  return FISHING_STATS.map((s): FishingStatValue => {
    const fromLevel = Math.round(Math.max(0, fishingLevel - 1) * s.perLevel);
    const fromGear = Math.round(gear[s.id]);
    return { id: s.id, base: s.base, fromLevel, fromGear, value: s.base + fromLevel + fromGear };
  });
}

// ---- TACKLE POWER + LURE (the fish gates) -----------------------------------

/** The rig slots that contribute TACKLE POWER (the lure does not). Also the four slots of the upgrade track,
 *  in display order — see the UPGRADE TRACK section below. */
export const RIG_SLOT_IDS: RigSlot[] = ["rod", "reel", "handle", "hook"];

/**
 * The player's total TACKLE POWER = sum of the `power` of every equipped RIG piece. A fish with `needsPower`
 * can't be hooked until this reaches it, so upgrading the rig (via levels) is what opens the bigger fish.
 * A bare rig (nothing equipped) has power 0 — the starter rod gives the first point.
 */
export function deriveTacklePower(equipped: EquippedGear): number {
  let power = 0;
  for (const slot of RIG_SLOT_IDS) { const it = equipped[slot] ? gearItem(equipped[slot]!) : undefined; power += it?.power ?? 0; }
  return power;
}

// ---- THE RIG UPGRADE TRACK --------------------------------------------------
//
// THE MODEL. Each of the four rig slots holds ONE piece at a TIER of 1..8, and gold buys the NEXT tier for
// that slot. There is nothing to choose: the piece BECOMES the next tier in place. The eight flavour names per
// slot in RIG_TIERS are the rungs of that ladder rather than eight separate products, and its `price` column
// now reads as the COST TO UPGRADE INTO a tier rather than to buy it outright — the numbers are unchanged, so
// both of the properties that column was tuned for still hold: power per piece still tops out at 12 (a full
// rig of 48, the first that clears the Aetherling's 46 — see RIG_TIERS), and the unlock levels still land
// against the XP curve rather than evenly in levels.
//
// WHY IT REPLACED A SHOP. The old model BOUGHT each tier per slot and kept them all, so a fully-kitted player
// carried 32 rig pieces of which 31 were strictly worse than the one equipped. Nothing in the game ever made
// equipping a lower tier worth doing — no weight limit, no situational trade-off, no set bonus — so the other
// 31 were pure inventory noise wearing the costume of a choice.
//
// THE LURE IS NOT ON THIS TRACK, and must not be put on one. Lures are a FAMILY gate, not a ladder: 25 fish
// in fishConds.ts carry a `needsLure` that only one family satisfies (spoon 5, void 4, fly/glow/jig/prism 3
// each, popper/spinner 2 each), so collapsing the eight lures into a linear track would orphan the fish
// behind every family the track had climbed past. The lure stays bought-and-swapped.
//
// HOW IT MEETS THE REST OF THE FILE. A tier RESOLVES to the canonical item id for that slot+tier (`rigItemId`
// — tier 5 of the rod is `rod5`), which is the id everything downstream already speaks: `gearRolls` is keyed
// by it, and the equip/stat/power path resolves through `gearItem(id)`. So the track is a different way to
// SPELL an equipped set, not a different set — `rigEquipped` encodes four tiers into an `EquippedGear` and
// `rigTiers` decodes one back, and nothing below this section had to change.

/** The number of tiers on the track. The top rung is `MAX_RIG_TIER`; `nextUpgrade` returns null there. */
export const MAX_RIG_TIER = RIG_TIERS.length;

function clampTier(t: number): number {
  return Math.max(1, Math.min(MAX_RIG_TIER, Math.floor(Number.isFinite(t) ? t : 1)));
}

/** The player's rig as four TIER NUMBERS — one per slot, each 1..8. This is the whole of the rig's progress;
 *  there is no owned-list beside it, because a slot holds exactly one piece. Tier 1 is the free starter. */
export type RigTiers = Record<RigSlot, number>;

/** A brand-new rig: every slot on its free tier-1 starter piece. */
export const STARTER_RIG_TIERS: RigTiers = { rod: 1, reel: 1, handle: 1, hook: 1 };

/** The canonical catalogue item id a slot+tier resolves to (`rigItemId("rod", 5)` → `"rod5"`). This is the id
 *  `gearRolls` is keyed by and the one `gearItem` resolves, so it is the join between the track and the rest
 *  of the gear system. The tier is clamped into range, so an out-of-band number can never name a missing id. */
export function rigItemId(slot: RigSlot, tier: number): string {
  return `${slot}${clampTier(tier)}`;
}

/** The catalogue piece a slot currently holds. Always defined — `rigItemId` clamps into the real table. */
export function rigPiece(slot: RigSlot, tier: number): GearItem {
  return gearItem(rigItemId(slot, tier))!;
}

/** Encode four tiers as an `EquippedGear`, so the tier representation can be handed to everything that already
 *  reads equipped item ids (`deriveLoadout`, `deriveTacklePower`, `rodAppearance`, …). The LURE is passed
 *  through untouched — it is not on the track and keeps whatever the player has equipped. */
export function rigEquipped(tiers: RigTiers, lure?: string): EquippedGear {
  const out: EquippedGear = {};
  for (const slot of RIG_SLOT_IDS) out[slot] = rigItemId(slot, tiers[slot]);
  if (lure) out.lure = lure;
  return out;
}

/** Decode an `EquippedGear` back to the four tier numbers. The inverse of `rigEquipped` for any set the track
 *  produced; for a legacy set it reads whichever tier each slot happens to hold, with empty slots at 1. */
export function rigTiers(equipped: EquippedGear): RigTiers {
  return {
    rod: rigTierOf(equipped, "rod"),
    reel: rigTierOf(equipped, "reel"),
    handle: rigTierOf(equipped, "handle"),
    hook: rigTierOf(equipped, "hook"),
  };
}

/** Total TACKLE POWER of a rig expressed as tiers — the same sum `deriveTacklePower` takes off an equipped
 *  set, without having to build one. A full tier-8 rig is 48; a starter rig is 4. */
export function rigTacklePower(tiers: RigTiers): number {
  let power = 0;
  for (const slot of RIG_SLOT_IDS) power += rigPiece(slot, tiers[slot]).power ?? 0;
  return power;
}

/** What upgrading a slot one rung buys: the tier it moves to, that tier's flavour name, what it costs, the
 *  fishing level it needs, and the tackle power it adds. `powerGain` is the DELTA (next − current), which is
 *  what an upgrade prompt wants to show; `power` is the new piece's own contribution. */
export interface RigUpgrade {
  slot: RigSlot;
  /** the tier being upgraded FROM (1..7). */
  fromTier: number;
  /** the tier being upgraded TO (2..8). */
  toTier: number;
  /** the canonical item id the slot becomes (`rod5`) — what `gearRolls` will be keyed by. */
  itemId: string;
  /** the new tier's flavour name ("Graphite Rod"). */
  name: string;
  icon: string;
  /** gold cost to upgrade INTO this tier (RIG_TIERS' `price` column, read as an upgrade cost). */
  price: number;
  /** fishing level required to upgrade into this tier (RIG_TIERS' `unlock` column). */
  unlockLevel: number;
  /** the new piece's own tackle power. */
  power: number;
  /** tackle power GAINED by the upgrade (new power − current power). */
  powerGain: number;
}

/** The next rung for a slot, or NULL when the slot is already at the top tier (8) — which is the one signal a
 *  caller needs to render "fully upgraded" rather than a price. */
export function nextUpgrade(slot: RigSlot, tier: number): RigUpgrade | null {
  const from = clampTier(tier);
  if (from >= MAX_RIG_TIER) return null;
  const to = from + 1;
  const next = rigPiece(slot, to);
  const cur = rigPiece(slot, from);
  return {
    slot, fromTier: from, toTier: to, itemId: next.id, name: next.name, icon: next.icon,
    price: next.price ?? 0,
    unlockLevel: next.unlockLevel ?? 0,
    power: next.power ?? 0,
    powerGain: (next.power ?? 0) - (cur.power ?? 0),
  };
}

/** Whether a slot's next upgrade can be bought right now, with THE TWO GATES REPORTED SEPARATELY. They are
 *  split because they are different problems with different answers: too low a level means "go fish", not
 *  enough gold means "go sell" — a single boolean would leave the UI unable to say which. `maxed` is the third
 *  state (no next tier at all), where both gate flags are false and `upgrade` is null. */
export interface RigUpgradeCheck {
  /** the rung being checked, or null when the slot is already at tier 8. */
  upgrade: RigUpgrade | null;
  /** true when the slot is at the top tier — there is nothing left to buy. */
  maxed: boolean;
  /** fishing level is high enough for the next tier. */
  levelMet: boolean;
  /** the player can afford it. */
  goldMet: boolean;
  /** both gates pass and a next tier exists — the only state in which an upgrade may be applied. */
  ok: boolean;
  /** how many more fishing levels are needed (0 when `levelMet`). */
  levelShort: number;
  /** how much more gold is needed (0 when `goldMet`). */
  goldShort: number;
}

export function canUpgrade(slot: RigSlot, tier: number, fishingLevel: number, coins: number): RigUpgradeCheck {
  const upgrade = nextUpgrade(slot, tier);
  if (!upgrade) {
    return { upgrade: null, maxed: true, levelMet: false, goldMet: false, ok: false, levelShort: 0, goldShort: 0 };
  }
  const levelShort = Math.max(0, upgrade.unlockLevel - fishingLevel);
  const goldShort = Math.max(0, upgrade.price - coins);
  const levelMet = levelShort === 0;
  const goldMet = goldShort === 0;
  return { upgrade, maxed: false, levelMet, goldMet, ok: levelMet && goldMet, levelShort, goldShort };
}

/** Apply an upgrade: the same tiers with ONE slot moved up a rung. Pure — returns a new `RigTiers` and never
 *  mutates the input. Returns the tiers UNCHANGED at tier 8, so a caller that skipped `canUpgrade` cannot walk
 *  off the end of the table. It does NOT check the gates or spend the gold; `canUpgrade` owns that, and the
 *  caller owns the coins. */
export function applyUpgrade(tiers: RigTiers, slot: RigSlot): RigTiers {
  const next = nextUpgrade(slot, tiers[slot]);
  return next ? { ...tiers, [slot]: next.toTier } : tiers;
}

/** The whole track for one slot — all eight rungs with the player's current position marked. For a UI that
 *  wants to draw the ladder rather than just the next step. */
export interface RigRung {
  tier: number;
  itemId: string;
  name: string;
  icon: string;
  price: number;
  unlockLevel: number;
  power: number;
  /** the player already holds this tier (it is at or below their current one). */
  owned: boolean;
  /** this is the tier the slot is on right now. */
  current: boolean;
}

export function rigTrack(slot: RigSlot, tier: number): RigRung[] {
  const cur = clampTier(tier);
  return RIG_TIERS.map((r): RigRung => {
    const it = rigPiece(slot, r.tier);
    return {
      tier: r.tier, itemId: it.id, name: it.name, icon: it.icon,
      price: it.price ?? 0, unlockLevel: it.unlockLevel ?? 0, power: it.power ?? 0,
      owned: r.tier <= cur, current: r.tier === cur,
    };
  });
}

/** The lure FAMILY id currently equipped (from the lure slot), or undefined if no lure is on. A fish with
 *  `needsLure` bites only when this equals its required lure family. */
export function equippedLureId(equipped: EquippedGear): string | undefined {
  const it = equipped.lure ? gearItem(equipped.lure) : undefined;
  return it?.lureId;
}

/**
 * The packaged loadout the fishing sim reads each cast — tackle power + lure + the resolved KNOB VALUES (from
 * the stat points, so they already include base + level + gear). One object so App can compute it once.
 *  • biteRate/lineStrength/drainPower — knob multipliers (≥1). biteRate ALSO folds in luck's bite bonus.
 *  • luckRarity — the extra tilt toward rarer fish in the WHICH-fish roll (1 = none). Driven by the luck stat.
 *  • intuition — a 0..1 FRACTION, not a multiplier. The odd one out; see `intuitionFraction`.
 */
export interface FishingLoadout {
  /** legacy TACKLE POWER (sum of rig `power` fields) — still gates fish `needsPower`. NOT the "Power" stat. */
  power: number;
  lure?: string;
  biteRate: number;        // ÷ bite wait — speed only (biteRate stat, folds in luck's share)
  lineStrength: number;    // × line snap tolerance + escape-fill reduction
  drainPower: number;      // × fish-stamina drain speed  (the "Power" stat)
  luckRarity: number;      // rarity tilt (luck stat)
  intuition: number;       // 0..1 intuition FRACTION (drives shadow spawn rate + reveal depth)
}

/** The share of luck's magnitude that also speeds up bites (the rest of luck tilts rarity). Exported because
 *  the Tackle readout (ui/FishingGearModal.tsx) quotes it. */
export const LUCK_BITE_SLOPE = 0.35;

export function deriveLoadout(equipped: EquippedGear, fishingLevel: number, rolls?: Record<string, GearRoll>): FishingLoadout {
  const eff = effectiveEquipped(equipped);   // fill empty rig slots with the free starter pieces
  const stats = deriveFishingStats(eff, fishingLevel, rolls);
  const pts = (id: FishingStatId) => stats.find((s) => s.id === id)?.value ?? 0;
  const luckMult = statEffect("luck", pts("luck"));                 // e.g. 1.0 → ~2.2 over the luck range
  // luck affects BITE RATE (a gentle share of its magnitude) AND fish RARITY (the which-fish tilt).
  const luckBite = 1 + (luckMult - 1) * LUCK_BITE_SLOPE;           // ~35% of luck's magnitude → faster bites
  const luckRarity = luckMult;                                      // full luck magnitude tilts the roll
  const intuition = intuitionFraction(pts("intuition"));
  return {
    power: deriveTacklePower(eff),
    lure: equippedLureId(eff),
    biteRate: statEffect("biteRate", pts("biteRate")) * luckBite,
    lineStrength: statEffect("lineStrength", pts("lineStrength")),
    drainPower: statEffect("power", pts("power")),
    luckRarity,
    intuition,
  };
}

/** A neutral loadout (nothing equipped, level 1) — power 0, all knobs baseline. A default before gear exists. */
export const NEUTRAL_LOADOUT: FishingLoadout = { power: 0, lure: undefined, biteRate: 1, lineStrength: 1, drainPower: 1, luckRarity: 1, intuition: 0 };
